// Активный запуск: переживает перезагрузку вкладки и потерю сети.
//
// Это не удобство, а выполнение обещания экрана 05 — «можно закрыть». Сервер
// свою половину держит: уход клиента запуск не отменяет, а поток при
// подключении всегда первым отдаёт снимок с накопленным текстом
// (apps/api/src/routes/runs.ts). Клиенту остаётся не потерять номер запуска и
// вернуться к нему.
//
// Возврат на вкладку проверяется снимком, а не доверием к потоку. Усыплённая
// вкладка часто получает мёртвое соединение без события `error`: браузер
// заморозил таймеры, сервер закрыл сокет по таймауту, а EventSource всё ещё
// считает себя открытым. Такой поток молчит, и экран остался бы на этапе,
// который давно прошёл, — то самое «зелёная галочка, а на проде старый код»,
// только в интерфейсе.
//
// ⚠️ СПИСОК ЧАТОВ СЛЕВА ОБНОВЛЯЕТСЯ ОТСЮДА, а не с экрана чата (SMY-355,
// 05.10.2026). До этого его перечитывал экран чата, дождавшись конца запуска,
// — то есть только пока был смонтирован. Кто нажимал «Новый запрос» посреди
// работы, свою задачу в «Чатах» не находил ни во время работы, ни после:
// вкладка писала «● Делаю…», потом «Не получилось», а чата в списке не было
// до перезагрузки. Запуск живёт здесь, значит и следствия его конца — здесь:
// после старта (вопрос уже в ленте, новый чат заведён) и по концу работы.

import {
  createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import { isTerminal } from '@smyslika/core'
import { attempts, OfflineError, type RunState, type StartRunInput } from '@smyslika/api-client'

import { useSession } from './session.tsx'
import { useWorkspace } from './workspace.tsx'

const RUN_KEY = 'smyslika.run'

const remember = (runId: string | null): void => {
  try {
    if (runId == null) localStorage.removeItem(RUN_KEY)
    else localStorage.setItem(RUN_KEY, runId)
  } catch {
    /* приватный режим: запуск переживёт вкладку, но не её перезагрузку */
  }
}

const recall = (): string | null => {
  try {
    return localStorage.getItem(RUN_KEY)
  } catch {
    return null
  }
}

const CHAT_KEY = 'smyslika.run.chat'

/**
 * Чат, в котором идёт запуск.
 *
 * Хранится рядом с номером запуска и по той же причине: экран чата обязан
 * узнать СВОЙ запуск. Иначе перезагрузка вкладки на одном чате показала бы
 * прогресс соседнего — состояние-то восстановилось, а к чему оно относится,
 * никто не запомнил. Снимок запуска чат не называет: телеметрия запусков
 * контента не содержит, и связь с лентой живёт только в базе.
 */
const rememberChat = (chatId: string | null): void => {
  try {
    if (chatId == null) localStorage.removeItem(CHAT_KEY)
    else localStorage.setItem(CHAT_KEY, chatId)
  } catch {
    /* приватный режим */
  }
}

const recallChat = (): string | null => {
  try {
    return localStorage.getItem(CHAT_KEY)
  } catch {
    return null
  }
}

/**
 * Состояние связи. Три, а не два: «сети нет» и «сервер не отвечает» — разные
 * беды, и экран 05 говорит о них разное. Первое пользователь чинит сам.
 */
export type Connection = 'online' | 'reconnecting' | 'offline'

export interface ActiveRunValue {
  run: RunState | null
  /** Чат, в котором идёт запуск. По нему экран узнаёт свой прогресс. */
  chatId: string | null
  /** Запуск ничего не спишет: уточнение в уже оплаченном чате. */
  free: boolean
  connection: Connection
  /** Восстанавливаем ли запуск прямо сейчас — до первого снимка. */
  restoring: boolean
  /**
   * Запуск, который сервер перестал знать, не дав нам увидеть конец: `gone`
   * после рестарта при выкладке или пустой снимок после долгого отсутствия
   * (SMY-352). Экран его чата перечитывает ленту — объяснение исхода уже там.
   */
  lost: string | null
  /**
   * Завести запуск. Возвращает чат, в который он лёг: у нового его ещё нет.
   * `key` — ключ попытки (SMY-449): повтор с ним получает тот же запуск;
   * `signal` обрывает ожидание брошенного повтора.
   */
  start(
    input: StartRunInput,
    key?: string,
    signal?: AbortSignal,
    notice?: string,
  ): Promise<{ runId: string; chatId?: string; free: boolean }>
  /**
   * Строка списания экрана 03б у запуска, если шита не было (SMY-494).
   *
   * Живёт здесь, а не в экране: экран нового чата в приложении сменяется
   * экраном чата после старта, человек уходит с экрана, пока старт идёт, а
   * досылка после обрыва зовёт `start` из другого места — строка экрана
   * терялась во всех трёх случаях (ворота SMY-494, круг 2, ux-states-checker).
   */
  chargeOf(runId: string): string | undefined
  /**
   * Подхватить запуск, заведённый мимо `start` — бесплатную правку артефакта.
   *
   * Ручка правки заводит запуск сама и отдаёт его номер (SMY-54). Без этого
   * метода экран знал бы, что правка ушла, и не знал бы, чем она кончилась.
   */
  adopt(runId: string, chatId: string, free?: boolean): void
  /**
   * С чем заводили запуск этой вкладкой — чтобы повторить сбой тем же входом:
   * текст, сценарий, файл и чат (SMY-352). Нет у запусков, заведённых мимо
   * `start`, — правок результата, — и у прошлых вкладок: вход живёт в памяти.
   */
  inputOf(runId: string): StartRunInput | undefined
  cancel(): Promise<void>
  /** Убрать завершённый запуск с экрана. Сам он не исчезает. */
  dismiss(): void
}

const ActiveRunContext = createContext<ActiveRunValue | null>(null)

export function useActiveRun(): ActiveRunValue {
  const value = use(ActiveRunContext)
  if (value == null) throw new Error('useActiveRun вне ActiveRunProvider')
  return value
}

export function ActiveRunProvider({ children }: { children: ReactNode }) {
  const { runs, signedIn, refreshBalances, refreshMe, me } = useSession()
  const { refresh: refreshChats } = useWorkspace()
  const [run, setRun] = useState<RunState | null>(null)
  const [chatId, setChatId] = useState<string | null>(() => recallChat())
  const [free, setFree] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [lost, setLost] = useState<string | null>(null)
  const [connection, setConnection] = useState<Connection>(
    typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online',
  )

  /** Отписка от текущего потока. Ref, а не state: пересборки она не стоит. */
  const unsubscribe = useRef<(() => void) | null>(null)
  /**
   * Вход каждого запуска, заведённого через `start`. В памяти, а не в
   * хранилище: там лежал бы текст вопроса, а гостю веба обещано, что вкладка
   * его работу не переживёт (`token-store.ts`).
   */
  const inputs = useRef(new Map<string, StartRunInput>())
  /** Строки списания 03б по запускам (`chargeOf`). */
  const charges = useRef(new Map<string, string>())
  const runId = useRef<string | null>(null)

  const detach = useCallback(() => {
    unsubscribe.current?.()
    unsubscribe.current = null
  }, [])

  const subscribe = useCallback(
    (id: string) => {
      detach()
      unsubscribe.current = runs.stream(id, {
        onOpen: () => setConnection(navigator.onLine ? 'online' : 'offline'),
        onReconnecting: () => setConnection(navigator.onLine ? 'reconnecting' : 'offline'),
        onEvent: (event) => {
          setRun((prev) => {
            switch (event.type) {
              case 'state':
                return event.run
              case 'gone':
                return prev
              case 'step': {
                if (prev == null) return prev
                const steps = prev.steps.some((s) => s.id === event.step.id)
                  ? prev.steps.map((s) => (s.id === event.step.id ? event.step : s))
                  : [...prev.steps, event.step]
                return { ...prev, steps }
              }
              case 'source':
                return prev == null ? prev : { ...prev, sources: [...prev.sources, event.source] }
              case 'delta':
                return prev == null ? prev : { ...prev, text: prev.text + event.text }
              case 'artifact':
                return prev == null ? prev : { ...prev, artifact: event.artifact }
              case 'offer':
                return prev == null ? prev : { ...prev, offer: event.offer }
              case 'notice': {
                if (prev == null) return prev
                // Повтор гасит и сервер, но событие может прийти дважды при
                // переподключении: снимок уже несёт плашку, а следом идёт она
                // же отдельным событием.
                const seen = prev.notices ?? []
                if (seen.some((n) => n.text === event.notice.text)) return prev
                return { ...prev, notices: [...seen, event.notice] }
              }
              case 'done':
                return prev == null
                  ? prev
                  : {
                      ...prev,
                      status: event.outcome === 'cancelled' ? 'cancelled' : 'done',
                      outcome: event.outcome,
                      charged: event.charged,
                      ...(event.remaining != null ? { remaining: event.remaining } : {}),
                    }
            }
          })

          if (event.type === 'gone') {
            // Сервер такого запуска не знает: возвращаться некуда, и держать
            // номер в хранилище значило бы пытаться вечно.
            runId.current = null
            remember(null)
            setRun(null)
            setLost(id)
          }
          if (event.type === 'done') {
            runId.current = null
            remember(null)
            // Остаток пришёл в событии, но остальные категории мог задеть
            // соседний запуск — шапка обновляется целиком.
            void refreshBalances()
            // И карточка человека: именно здесь `hasResult` становится
            // истиной, и предложение завести пароль всплывает сразу после
            // первого готового ответа, а не при следующей перезагрузке.
            void refreshMe()
            // Ответ дописал ленту — чат поднялся в списке. См. шапку.
            void refreshChats()
          }
        },
      })
    },
    [detach, runs, refreshBalances, refreshMe, refreshChats],
  )

  /** Снимок — источник правды при возврате. Поток только догоняет. */
  const resync = useCallback(async () => {
    const id = runId.current ?? recall()
    if (id == null) return
    runId.current = id
    setChatId(recallChat())

    setRestoring(true)
    try {
      const state = await runs.snapshot(id)
      if (state == null) {
        // Запуск завершился и ушёл из памяти сервера, пока нас не было.
        runId.current = null
        remember(null)
        setRun(null)
        setLost(id)
        return
      }
      setRun(state)
      setConnection(navigator.onLine ? 'online' : 'offline')
      if (isTerminal(state.status)) {
        runId.current = null
        remember(null)
        detach()
        // Кончился, пока вкладка спала: события `done` не было, а лента
        // дописана — список перечитывается и здесь.
        void refreshChats()
      } else {
        subscribe(id)
      }
    } catch (e) {
      // Офлайн — не повод забывать запуск: он продолжается на сервере, и
      // вернуться к нему надо будет ровно тогда, когда появится сеть.
      if (e instanceof OfflineError) setConnection('offline')
    } finally {
      setRestoring(false)
    }
  }, [runs, subscribe, detach, refreshChats])

  // Сменился человек — молча заведённый новый гость (SMY-357) или вход в
  // другой аккаунт: запуск прежнего не его, и ни показывать его, ни
  // переспрашивать о нём нельзя — сервер на чужой запуск ответит отказом.
  // Первая отметка — не смена: перезагрузка восстанавливает свой запуск.
  const owner = useRef<string | null>(null)
  const who = me?.userId ?? null
  useEffect(() => {
    if (who == null) {
      // Вышел или сеанс кончился: его попытки с неизвестным исходом — его
      // текст, и вкладка не должна ни держать их, ни переспрашивать о них
      // (ворота ревью 4, security).
      if (owner.current != null) attempts.clear()
      return
    }
    const was = owner.current
    owner.current = who
    if (was == null || was === who) return
    detach()
    runId.current = null
    remember(null)
    rememberChat(null)
    inputs.current.clear()
    // Попытки с неизвестным исходом — тоже прежнего: его текст и его ключи.
    // Следующему в той же вкладке они не достаются (ворота ревью 4, security).
    attempts.clear()
    setRun(null)
    setChatId(null)
    setFree(false)
  }, [who, detach])

  // Строка над полем просит не обновлять страницу: пока у человека есть
  // попытка с неизвестным исходом — на экране или на паузе, — браузер
  // переспросит, в каком бы разделе кабинета человек ни был, а не только в
  // чате (ворота ревью 4, reviewer). SMY-449
  useEffect(() => {
    const ask = (e: BeforeUnloadEvent): void => {
      const user = owner.current
      if (user == null || !attempts.has(user)) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', ask)
    return () => window.removeEventListener('beforeunload', ask)
  }, [])

  // Восстановление после перезагрузки вкладки.
  useEffect(() => {
    if (signedIn !== true) return
    void resync()
    return detach
  }, [signedIn, resync, detach])

  // Возврат на вкладку и появление сети. Оба случая — повод спросить снимок,
  // а не полагаться на поток: он мог умереть молча.
  useEffect(() => {
    if (signedIn !== true) return

    const onVisible = (): void => {
      if (document.visibilityState === 'visible' && runId.current != null) void resync()
    }
    const onOnline = (): void => {
      setConnection('online')
      if (runId.current != null) void resync()
    }
    const onOffline = (): void => setConnection('offline')

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [signedIn, resync])

  // Заголовок вкладки вместо уведомления. Разбор — в apps/web/README.md:
  // Web Push здесь не подходит, а сказать «готово» человеку, ушедшему в
  // соседнюю вкладку, всё равно надо. Заголовок это делает без разрешений,
  // без service worker и без чужих серверов — и виден ровно там, куда
  // пользователь смотрит, когда вспоминает о задаче.
  useEffect(() => {
    const base = 'Смыслика'
    if (run == null) {
      document.title = base
      return
    }
    // `done` — это «сервер закончил», а не «получилось». Технический сбой
    // приходит тем же статусом с исходом `failed`, и написать над ним
    // «✓ Готово» значило бы соврать человеку, который на вкладку и смотрит.
    if (run.status === 'running') document.title = `● Делаю… · ${base}`
    else if (run.status === 'cancelled') document.title = `Отменено · ${base}`
    else if (run.status === 'done') {
      // Отказ по содержанию — не «готово»: в полосе вкладок это единственное,
      // что человек увидит, если ушёл в другую вкладку.
      document.title =
        run.outcome === 'failed' || run.outcome === 'blocked'
          ? `Не получилось · ${base}`
          : `✓ Готово · ${base}`
    } else document.title = base

    return () => {
      document.title = base
    }
  }, [run])

  const value = useMemo<ActiveRunValue>(
    () => ({
      run,
      chatId,
      free,
      connection,
      restoring,
      lost,
      async start(input, key, signal, notice) {
        const started = await runs.start(input, key, signal)
        if (notice != null) charges.current.set(started.runId, notice)
        runId.current = started.runId
        remember(started.runId)
        // Чат приходит от сервера, а не придумывается здесь: первое сообщение
        // заводит ленту само (POST /v1/runs), и её номер известен только там.
        const chat = started.chatId ?? input.chatId ?? null
        setChatId(chat)
        rememberChat(chat)
        // Вход для повтора — с чатом, в который запуск лёг: у нового чата
        // номера во входе не было, и повтор без него завёл бы второй чат.
        // Без ключа распознавания: связывается он один раз (`intents.link`).
        const { intentId: _linked, ...again } = input
        inputs.current.set(started.runId, { ...again, ...(chat != null ? { chatId: chat } : {}) })
        setFree(started.free === true)
        setRun(null)
        subscribe(started.runId)
        // Вопрос уже записан в ленту: новый чат появляется в списке сразу, а
        // не после ответа. См. шапку.
        void refreshChats()
        return { runId: started.runId, ...(started.chatId != null ? { chatId: started.chatId } : {}), free: started.free === true }
      },
      adopt(id, chat, isFree = true) {
        runId.current = id
        remember(id)
        setChatId(chat)
        rememberChat(chat)
        setFree(isFree)
        setRun(null)
        subscribe(id)
      },
      inputOf: (id) => inputs.current.get(id),
      chargeOf: (id) => charges.current.get(id),
      async cancel() {
        const id = runId.current
        if (id == null) return
        await runs.cancel(id)
        // Состояние придёт потоком: сервер решает, успел ли он отмениться.
      },
      dismiss() {
        detach()
        runId.current = null
        remember(null)
        rememberChat(null)
        setRun(null)
        setFree(false)
      },
    }),
    [run, chatId, free, connection, restoring, lost, runs, subscribe, detach, refreshChats],
  )

  return <ActiveRunContext value={value}>{children}</ActiveRunContext>
}
